import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Button,
  Chip,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Link from "@mui/material/Link";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { config, LabourAttandenceAPI, userData } from "../../config";

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

// Fri -> Thu business week from an anchor date (moment). Friday = 5.
const getWeekRange = (anchor) => {
  const d = anchor.clone();
  const offset = (d.day() - 5 + 7) % 7;
  const start = d.clone().subtract(offset, "days");
  const end = start.clone().add(6, "days");
  return { start, end };
};

function WeeklyLabourApproval() {
  const childRef = useRef();

  const [anchor, setAnchor] = useState(moment());
  const [rows, setRows] = useState([]);
  const [approverConfig, setApproverConfig] = useState({
    verifier_emp_id: 0,
    verifier_name: "",
    approver_emp_id: 0,
    approver_name: "",
  });
  const [verifying, setVerifying] = useState(false);
  const [approving, setApproving] = useState(false);

  const { start, end } = getWeekRange(anchor);
  const fromDate = start.format("YYYY-MM-DD");
  const toDate = end.format("YYYY-MM-DD");
  const weekLabel = `${start.format("DD MMM")} – ${end.format("DD MMM YY")}`;

  useEffect(() => {
    fetchWeek();
    fetchApproverConfig();
  }, [fromDate, toDate]);

  const fetchWeek = () => {
    config.method = "POST";
    config.data = JSON.stringify({
      intType: 13,
      txtFromDate: fromDate,
      txtToDate: toDate,
      intUserID: 0,
    });
    config.url = LabourAttandenceAPI;

    axios(config)
      .then(function (response) {
        setRows(response.data.result || []);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchApproverConfig = () => {
    config.method = "POST";
    config.data = JSON.stringify({ intType: 16 });
    config.url = LabourAttandenceAPI;

    axios(config)
      .then(function (response) {
        const cfg = response.data.result && response.data.result[0];
        if (cfg) {
          setApproverConfig(cfg);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  // Aggregates
  const totalCost = rows.reduce((sum, r) => sum + (Number(r.line_total) || 0), 0);
  const advance = rows.reduce(
    (sum, r) => sum + (Number(r.advance_deduction) || 0),
    0
  );
  const net = totalCost - advance;
  const manDays = rows.reduce(
    (sum, r) =>
      sum + (Number(r.full_day_count) || 0) + (Number(r.half_day_count) || 0),
    0
  );

  // weekStatus = MIN of per-sheet status (per distinct reference_no); 0 if no rows.
  const sheetStatus = {};
  rows.forEach((r) => {
    if (!(r.reference_no in sheetStatus)) {
      sheetStatus[r.reference_no] = Number(r.status) || 0;
    }
  });
  const statuses = Object.values(sheetStatus);
  const weekStatus = statuses.length ? Math.min(...statuses) : 0;

  const statusChip = () => {
    if (weekStatus === 4) {
      return <Chip label="Approved" sx={{ backgroundColor: "#2e7d32", color: "#fff" }} />;
    }
    if (weekStatus === 3) {
      return <Chip label="Submitted" sx={{ backgroundColor: "#1565c0", color: "#fff" }} />;
    }
    if (weekStatus === 2) {
      return <Chip label="Verified" sx={{ backgroundColor: "#009688", color: "#fff" }} />;
    }
    if (weekStatus === 1) {
      return <Chip label="Prepared" sx={{ backgroundColor: "#ed6c02", color: "#fff" }} />;
    }
    return <Chip label="No Data" sx={{ backgroundColor: "#9e9e9e", color: "#fff" }} />;
  };

  // Submit opens on the Friday after the week's Thursday (weekEnd + 1 day).
  const submitOpensOn = end.clone().add(1, "day");
  const submitDateReached = !moment().isBefore(submitOpensOn, "day");

  const empId = Number(userData?.emp_id);
  const isVerifier = empId === Number(approverConfig.verifier_emp_id);
  const isApprover = empId === Number(approverConfig.approver_emp_id);
  // canSubmit (weekly): verifier AND whole week Verified (status 2) AND
  // today >= weekEnd + 1 day (the following Friday or later).
  const canSubmit =
    isVerifier && rows.length > 0 && weekStatus === 2 && submitDateReached;
  // Whole week is verified but the Friday gate hasn't opened yet.
  const submitPending =
    isVerifier && rows.length > 0 && weekStatus === 2 && !submitDateReached;
  // canApprove (weekly): approver AND whole week Submitted (status 3).
  const canApprove =
    isApprover && rows.length > 0 && weekStatus === 3;

  const handleSubmit = () => {
    setVerifying(true);
    config.method = "POST";
    config.data = JSON.stringify({
      intType: 18,
      txtFromDate: fromDate,
      txtToDate: toDate,
      intUserID: userData.emp_id,
    });
    config.url = LabourAttandenceAPI;

    axios(config)
      .then(function (response) {
        setVerifying(false);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: response.data.message || "Week submitted for approval",
            messageType: "success",
          })
        );
        fetchWeek();
      })
      .catch(function (error) {
        setVerifying(false);
        console.log(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong",
            messageType: "error",
          })
        );
      });
  };

  const handleApprove = () => {
    setApproving(true);
    config.method = "POST";
    config.data = JSON.stringify({
      intType: 19,
      txtFromDate: fromDate,
      txtToDate: toDate,
      intUserID: userData.emp_id,
    });
    config.url = LabourAttandenceAPI;

    axios(config)
      .then(function (response) {
        setApproving(false);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: response.data.message || "Week approved",
            messageType: "success",
          })
        );
        fetchWeek();
      })
      .catch(function (error) {
        setApproving(false);
        console.log(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong",
            messageType: "error",
          })
        );
      });
  };

  // Group rows: project -> contractor -> grade lines
  const grouped = {};
  rows.forEach((r) => {
    const project = r.project_name || "—";
    const contractor = r.labour_name || "—";
    if (!grouped[project]) grouped[project] = {};
    if (!grouped[project][contractor]) grouped[project][contractor] = [];
    grouped[project][contractor].push(r);
  });

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Weekly Labour Approval
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Weekly Labour Approval</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        {/* Week navigator */}
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={() => setAnchor(anchor.clone().subtract(7, "days"))}
            >
              Prev
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={() => setAnchor(moment())}>
              This week
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              onClick={() => setAnchor(anchor.clone().add(7, "days"))}
            >
              Next
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {weekLabel}
            </Typography>
          </Grid>
          <Grid item>{statusChip()}</Grid>
        </Grid>

        {/* Summary */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Total Labour Cost
                </Typography>
                <Typography variant="h6">{formatCurrency(totalCost)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Advance
                </Typography>
                <Typography variant="h6">{formatCurrency(advance)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Net Payable
                </Typography>
                <Typography variant="h6">{formatCurrency(net)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Man-days
                </Typography>
                <Typography variant="h6">{manDays}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action buttons */}
        {(canSubmit || submitPending || canApprove) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(canSubmit || submitPending) && (
              <Grid item>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  startIcon={<DoneIcon />}
                  loading={verifying}
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  Submit to approver
                </LoadingButton>
                {submitPending && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Submit opens {submitOpensOn.format("DD MMM YYYY")}
                  </Typography>
                )}
              </Grid>
            )}
            {canApprove && (
              <Grid item>
                <LoadingButton
                  variant="contained"
                  color="success"
                  startIcon={<DoneAllIcon />}
                  loading={approving}
                  onClick={handleApprove}
                >
                  Approve week
                </LoadingButton>
              </Grid>
            )}
          </Grid>
        )}

        {/* Grouped detail */}
        {rows.length === 0 ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No labour entries for this week.
          </Typography>
        ) : (
          Object.keys(grouped).map((project, pIndex) => (
            <Card key={pIndex} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {project}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {Object.keys(grouped[project]).map((contractor, cIndex) => (
                  <div key={cIndex} style={{ marginBottom: "12px" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {contractor}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Grade</TableCell>
                          <TableCell>Work Type</TableCell>
                          <TableCell align="center">Full</TableCell>
                          <TableCell align="center">Half</TableCell>
                          <TableCell align="center">Partial</TableCell>
                          <TableCell align="center">Partial Hrs</TableCell>
                          <TableCell align="right">Advance</TableCell>
                          <TableCell align="right">Line Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {grouped[project][contractor].map((r, rIndex) => (
                          <TableRow key={rIndex}>
                            <TableCell>{r.labour_type_name || "-"}</TableCell>
                            <TableCell>{r.work_type_name || "-"}</TableCell>
                            <TableCell align="center">
                              {r.full_day_count || 0}
                            </TableCell>
                            <TableCell align="center">
                              {r.half_day_count || 0}
                            </TableCell>
                            <TableCell align="center">
                              {r.partial_count || 0}
                            </TableCell>
                            <TableCell align="center">
                              {r.partial_hours || 0}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(r.advance_deduction)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(r.line_total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))
        )}
      </Grid>
      <AlertMessage ref={childRef} />
    </Layout>
  );
}

export default WeeklyLabourApproval;
